import React, { useCallback, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Toast } from 'components/shared/Toaster';
import { deletePlaidConnection, getPlaidConnectionDeletions, getPlaidConnections } from 'actions/client';
import styles from '../profile.module.scss';

const CONFIRMATION_WORD = 'DELETE';
const DANGER = 'var(--status-declined)';

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');

const isLive = (connection) => connection.connectedAtPlaid && !connection.createdViaDemo;

const statusChip = (connection) => {
    if (connection.createdViaDemo) return <Chip size="small" label="Demo" />;
    if (connection.connectedAtPlaid) {
        return <Chip size="small" label="Connected" style={{ background: 'var(--status-approved)', color: '#fff' }} />;
    }
    return <Chip size="small" label="Link not finished" variant="outlined" />;
};

const RESULT_MESSAGES = {
    REMOVED: 'The bank connection was removed at Plaid and its data was deleted from Rox.',
    NOT_CONNECTED: 'The unfinished bank link was deleted from Rox. It was never connected at Plaid.',
    SKIPPED_DEMO: 'The demo bank connection was deleted from Rox. Demo data never reaches Plaid.',
    FAILED_DELETED_ANYWAY: 'The data was deleted from Rox, but the connection may still be active at Plaid. Remove it in the Plaid dashboard.',
};

const PLAID_REMOVAL_LABELS = {
    REMOVED: 'Removed',
    NOT_CONNECTED: 'Never connected',
    SKIPPED_DEMO: 'Demo, not sent',
    FAILED_DELETED_ANYWAY: 'Not confirmed',
};

const Detail = ({ label, value }) => (
    <Box display="flex" py={0.5}>
        <Typography variant="body2" color="textSecondary" style={{ width: 170, flexShrink: 0 }}>{label}</Typography>
        <Typography variant="body2">{value ?? '-'}</Typography>
    </Box>
);

const DeleteConnectionDialog = ({ connection, clientName, clientUuid, onClose, onDeleted }) => {
    const [step, setStep] = useState('review');
    const [understood, setUnderstood] = useState(false);
    const [typed, setTyped] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [plaidError, setPlaidError] = useState('');

    const goTo = (next) => {
        setStep(next);
        setUnderstood(false);
        setTyped('');
    };

    const submit = (deleteEvenIfPlaidFails) => {
        setDeleting(true);
        deletePlaidConnection(clientUuid, connection.ownerId, { confirmation: typed.trim(), deleteEvenIfPlaidFails })
            .then((result) => {
                Toast({
                    type: result.plaidRemoval === 'FAILED_DELETED_ANYWAY' ? 'warning' : 'success',
                    message: RESULT_MESSAGES[result.plaidRemoval],
                    autoClose: result.plaidRemoval === 'FAILED_DELETED_ANYWAY' ? false : undefined,
                });
                onDeleted();
            })
            .catch(async (response) => {
                const body = await response?.json?.().catch(() => null);
                const message = body?.message || 'The connection could not be deleted. Nothing was changed.';
                if (response?.status === 409) {
                    setPlaidError(message);
                    goTo('plaidFailed');
                } else {
                    Toast({ type: 'error', message });
                }
            })
            .finally(() => setDeleting(false));
    };

    const typedOk = typed.trim() === CONFIRMATION_WORD;

    return (
        <Dialog open onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
            {step === 'review' && (
                <>
                    <DialogTitle>Delete bank connection</DialogTitle>
                    <DialogContent>
                        <Alert severity="error" style={{ marginBottom: 16 }}>
                            This permanently deletes production data. It cannot be undone.
                        </Alert>
                        <Detail label="Client" value={clientName} />
                        <Detail label="Owner" value={connection.ownerName} />
                        <Detail label="Application" value={connection.applicationName} />
                        <Detail label="Application UUID" value={connection.applicationId} />
                        <Detail label="Status" value={statusChip(connection)} />
                        <Detail label="Bank (Plaid institution)" value={connection.institutionId} />
                        <Detail label="Plaid item" value={connection.itemId} />
                        <Detail label="Accounts" value={connection.accounts} />
                        <Detail label="Transactions" value={connection.transactions} />
                        <Detail label="Connected on" value={formatDate(connection.grantedAt)} />

                        <Box mt={2}>
                            <Typography variant="subtitle2">What will happen</Typography>
                            <ul style={{ paddingLeft: 20, margin: '4px 0 0' }}>
                                {isLive(connection) && (
                                    <li><Typography variant="body2">Rox asks Plaid to remove the connection first. Rox can no longer read this bank, and Plaid stops billing for it.</Typography></li>
                                )}
                                <li><Typography variant="body2">All of this owner's bank data is deleted from Rox: accounts, balances, transactions, recurring payments, account holder details and assets reports.</Typography></li>
                                <li><Typography variant="body2">The Bank Transactions views of this application will be empty.</Typography></li>
                                <li><Typography variant="body2">To get bank data again, the owner has to connect their bank from scratch.</Typography></li>
                            </ul>
                        </Box>

                        <FormControlLabel
                            style={{ marginTop: 12 }}
                            control={<Checkbox checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />}
                            label="I understand this deletes the data permanently and cannot be undone"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button variant="outlined" style={understood ? { color: DANGER, borderColor: DANGER } : undefined}
                                disabled={!understood} onClick={() => goTo('type')}>
                            Continue
                        </Button>
                    </DialogActions>
                </>
            )}

            {step === 'type' && (
                <>
                    <DialogTitle>Final confirmation</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" gutterBottom>
                            You are about to delete the bank connection of <strong>{connection.ownerName}</strong> for
                            client <strong>{clientName}</strong>.
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            Type <strong>{CONFIRMATION_WORD}</strong> to confirm.
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            placeholder={CONFIRMATION_WORD}
                            disabled={deleting}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
                        <Button variant="contained" style={typedOk && !deleting ? { background: DANGER, color: '#fff' } : undefined}
                                disabled={!typedOk || deleting} onClick={() => submit(false)}>
                            {deleting ? <CircularProgress size={22} color="inherit" /> : 'Delete permanently'}
                        </Button>
                    </DialogActions>
                </>
            )}

            {step === 'plaidFailed' && (
                <>
                    <DialogTitle>Plaid did not confirm the removal</DialogTitle>
                    <DialogContent>
                        <Alert severity="warning" style={{ marginBottom: 16 }}>
                            {plaidError}
                        </Alert>
                        <Typography variant="body2" gutterBottom>
                            Plaid may have removed this connection already, or the request may have failed. If you delete
                            from Rox anyway and the connection is still active, Plaid keeps billing for it and keeps
                            sending updates. Rox will no longer have the token needed to remove it, so it would have to
                            be removed by hand in the Plaid dashboard.
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            The safe choice is to cancel and try again later.
                        </Typography>
                        <FormControlLabel
                            control={<Checkbox checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />}
                            label="I will check the Plaid dashboard and remove the connection there if needed"
                        />
                        <TextField
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            placeholder={`Type ${CONFIRMATION_WORD} to delete from Rox anyway`}
                            disabled={deleting}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" color="primary" onClick={onClose} disabled={deleting}>Cancel</Button>
                        <Button variant="outlined" style={understood && typedOk && !deleting ? { color: DANGER, borderColor: DANGER } : undefined}
                                disabled={!understood || !typedOk || deleting} onClick={() => submit(true)}>
                            {deleting ? <CircularProgress size={22} color="inherit" /> : 'Delete from Rox anyway'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

/**
 * Rox-admin only: the client's Plaid bank connections, one row per owner, with a guarded delete
 * (admin/plaid-connections endpoints).
 */
const PlaidConnectionsTab = ({ clientUuid, clientName }) => {
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [showUnfinished, setShowUnfinished] = useState(true);
    const [toDelete, setToDelete] = useState(null);

    const [deletions, setDeletions] = useState([]);

    const fetchConnections = useCallback(() => {
        setLoading(true);
        setLoadFailed(false);
        getPlaidConnections(clientUuid)
            .then((res) => setConnections(res || []))
            .catch(() => setLoadFailed(true))
            .finally(() => setLoading(false));
        getPlaidConnectionDeletions(clientUuid)
            .then((res) => setDeletions(res || []))
            .catch(() => setDeletions([]));
    }, [clientUuid]);

    useEffect(fetchConnections, [fetchConnections]);

    const visible = showUnfinished ? connections : connections.filter((c) => c.connectedAtPlaid);
    const liveCount = connections.filter(isLive).length;

    return (
        <Card>
            <div className={styles.profileContainer}>
                <div className={styles.profileHeader}>
                    <div>
                        <h5>Plaid Connections</h5>
                        <Typography variant="body2" color="textSecondary">
                            Bank connections made by this client's owners through Plaid. {liveCount} connected at Plaid, {connections.length - liveCount} other.
                        </Typography>
                    </div>
                    <Button variant="outlined" color="primary" onClick={fetchConnections} disabled={loading}>Refresh</Button>
                </div>

                <Alert severity="warning" style={{ margin: '16px 0' }}>
                    Deleting a connection removes it at Plaid and permanently deletes the owner's bank data in Rox.
                    This acts on live data and cannot be undone.
                </Alert>

                <FormControlLabel
                    control={<Checkbox checked={showUnfinished} onChange={(e) => setShowUnfinished(e.target.checked)} />}
                    label="Show links that were started but never finished"
                />

                {loading && <div className="text-center p-4"><CircularProgress /></div>}

                {!loading && loadFailed && (
                    <div className="text-center p-4">
                        <p>Could not load the Plaid connections.</p>
                        <Button variant="outlined" color="primary" onClick={fetchConnections}>Retry</Button>
                    </div>
                )}

                {!loading && !loadFailed && visible.length === 0 && (
                    <Typography className="text-center p-4" color="textSecondary">This client has no Plaid connections.</Typography>
                )}

                {!loading && !loadFailed && visible.length > 0 && (
                    <Box style={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Owner</TableCell>
                                <TableCell>Application</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Bank</TableCell>
                                <TableCell align="right">Accounts</TableCell>
                                <TableCell align="right">Transactions</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Connected on</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map((connection) => (
                                <TableRow key={connection.ownerId}>
                                    <TableCell>{connection.ownerName || '-'}</TableCell>
                                    <TableCell>
                                        {connection.applicationName || '-'}
                                        <Typography variant="caption" display="block" color="textSecondary"
                                                    style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                            {connection.applicationId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {statusChip(connection)}
                                        {connection.itemErrorCode && (
                                            <Typography variant="caption" display="block" style={{ color: DANGER }}>
                                                {connection.itemErrorCode}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{connection.institutionId || '-'}</TableCell>
                                    <TableCell align="right">{connection.accounts}</TableCell>
                                    <TableCell align="right">{connection.transactions}</TableCell>
                                    <TableCell>{formatDate(connection.requestedAt)}</TableCell>
                                    <TableCell>{formatDate(connection.grantedAt)}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" variant="outlined" style={{ color: DANGER, borderColor: DANGER }}
                                                onClick={() => setToDelete(connection)}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </Box>
                )}

                {deletions.length > 0 && (
                    <Box mt={4}>
                        <h6>Deleted connections</h6>
                        <Box style={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Deleted on</TableCell>
                                        <TableCell>Deleted by</TableCell>
                                        <TableCell>Owner</TableCell>
                                        <TableCell>Application</TableCell>
                                        <TableCell>Bank</TableCell>
                                        <TableCell>At Plaid</TableCell>
                                        <TableCell align="right">Rows deleted</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {deletions.map((deletion) => (
                                        <TableRow key={`${deletion.deletedAt}-${deletion.applicationId}`}>
                                            <TableCell>{formatDate(deletion.deletedAt)}</TableCell>
                                            <TableCell>{deletion.deletedBy || '-'}</TableCell>
                                            <TableCell>{deletion.ownerName || '-'}</TableCell>
                                            <TableCell>
                                                {deletion.applicationName || '-'}
                                                <Typography variant="caption" display="block" color="textSecondary"
                                                            style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                    {deletion.applicationId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{deletion.institutionId || '-'}</TableCell>
                                            <TableCell>{PLAID_REMOVAL_LABELS[deletion.plaidRemoval] || deletion.plaidRemoval}</TableCell>
                                            <TableCell align="right">{deletion.deletedRows}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                )}
            </div>

            {toDelete && (
                <DeleteConnectionDialog
                    connection={toDelete}
                    clientName={clientName}
                    clientUuid={clientUuid}
                    onClose={() => setToDelete(null)}
                    onDeleted={() => {
                        setToDelete(null);
                        fetchConnections();
                    }}
                />
            )}
        </Card>
    );
};

export default PlaidConnectionsTab;
